/**
 * Auto-build Help Video Tour scenes from curated help narrations.
 */

import { getHelpNarration } from '../../common/helpNarrations';
import { pickFrameKey } from './frames';

function sectionSpeechText(section) {
  if (!section) return '';
  if (Array.isArray(section.turns)) {
    return section.turns.map((t) => t?.speech || '').filter(Boolean).join(' ');
  }
  return section.speech || '';
}

function cleanBlurb(text, maxLen = 140) {
  const cleaned = String(text || '')
    .replace(/\s+/g, ' ')
    .replace(/[—–]/g, ', ')
    .trim();
  if (cleaned.length <= maxLen) return cleaned;
  const cut = cleaned.slice(0, maxLen);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > 60 ? cut.slice(0, lastSpace) : cut).trim()}…`;
}

function slugId(title, idx) {
  const base = String(title || `scene-${idx}`)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 40);
  return base || `scene-${idx}`;
}

/** Build 1–2 callouts from title keywords. */
export function calloutsFromTitle(title = '') {
  const t = title.toLowerCase();
  const out = [];
  const push = (label, x, y) => {
    if (out.length < 2) out.push({ label, x, y });
  };

  if (/welcome|intro|overview|tour/.test(t)) {
    push('Start here', 22, 30);
    push('Follow the chapters', 68, 58);
  } else if (/find|nav|sidebar|menu|where/.test(t)) {
    push('Project dropdown', 42, 14);
    push('Left navigation', 16, 48);
  } else if (/matrix|grid|glance|column/.test(t)) {
    push('Identity columns', 14, 38);
    push('Progress & totals', 62, 38);
  } else if (/freeze|lock|credit|weight/.test(t)) {
    push('Rules of Credit', 70, 16);
    push('Freeze for project', 55, 58);
  } else if (/shop|fabricat/.test(t)) {
    push('Shop stages', 50, 42);
  } else if (/field|install/.test(t)) {
    push('Field steps', 35, 36);
    push('Weighted total', 78, 62);
  } else if (/import|export|data in|data out/.test(t)) {
    push('Import', 30, 40);
    push('Export', 70, 40);
  } else if (/trend|history|chart/.test(t)) {
    push('Open a row', 18, 40);
    push('Trend chart', 65, 48);
  } else if (/analytics|report|dashboard|kpi/.test(t)) {
    push('Analytics', 50, 36);
    push('Reports', 20, 70);
  } else if (/schedule|activity/.test(t)) {
    push('Activity link', 22, 40);
    push('Schedule % complete', 70, 55);
  } else if (/hold|comment|teammate|collab/.test(t)) {
    push('On hold', 80, 36);
    push('Who is here', 88, 14);
  } else   if (/checklist|first week|getting started|day/.test(t)) {
    push('Day-one checklist', 50, 48);
  } else if (/labour|op code|opcode|coding structure/.test(t)) {
    push('Maestro catalogue', 18, 38);
    push('Admin loads the job', 72, 38);
  } else if (/timesheet|daily|weekly/.test(t)) {
    push('Daily or Weekly', 50, 40);
  } else if (/form|detail|edit/.test(t)) {
    push('Fill the form', 40, 45);
  } else {
    push(title.slice(0, 36) || 'This chapter', 50, 42);
  }
  return out;
}

/**
 * Build tour scenes from `getHelpNarration(guideId)`.
 * One scene per narration section.
 */
export function buildScenesFromNarration(guideId, options = {}) {
  const sections = getHelpNarration(guideId);
  if (!Array.isArray(sections) || !sections.length) return [];

  const defaultFrame = options.defaultFrame || null;

  return sections.map((section, idx) => {
    const speech = sectionSpeechText(section);
    const title = section.title || `Chapter ${idx + 1}`;
    const haystack = `${title} ${speech}`;
    return {
      id: slugId(title, idx),
      title,
      icon: section.icon || '🎬',
      blurb: cleanBlurb(speech),
      callouts: calloutsFromTitle(title),
      frame: defaultFrame || pickFrameKey(haystack),
    };
  });
}
